/* SwipeableRow.jsx — calm operational swipe wrapper.
 *
 * Wraps a single row in a positioned container that reveals
 * action backdrops underneath as the user swipes. Mobile-only
 * (touch + coarse pointer). Desktop renders the row identically
 * to the un-wrapped version — no visual change.
 *
 * Right swipe → "Mark Progressed"  (emerald, calm)
 * Left swipe  → "Request Update"   (slate, neutral)
 *
 * Doctrine:
 *   - Almost invisible until used.
 *   - Snap-back via spring (no bounce, no elastic overscroll).
 *   - Action backgrounds use restrained tones — never alarm red.
 *   - Composable: works equally as a <div> wrapper or as a
 *     <tbody>-aware <tr> wrapper for table rows.
 */
import { Check, MessageSquare } from "lucide-react";
import { useSwipeActions, useCoarsePointer } from "../../hooks/useSwipeActions";

const PROGRESSED_TONE = {
  fg: "#047857",
  bg: "rgba(16,185,129,0.10)",
  chip: "rgba(16,185,129,0.18)",
};
const REQUEST_TONE = {
  fg: "#475569",
  bg: "rgba(100,116,139,0.10)",
  chip: "rgba(100,116,139,0.18)",
};

const ACTIVE_PROGRESS = 0.45; // fraction of threshold before label appears

function ActionBackdrop({ progressX }) {
  // Active edge — emerald reveals on right-swipe, slate on left.
  if (progressX === 0) return null;
  const isRight = progressX > 0;
  const tone = isRight ? PROGRESSED_TONE : REQUEST_TONE;
  const label = isRight ? "Mark Progressed" : "Request Update";
  const Icon  = isRight ? Check : MessageSquare;
  // Opacity ramps from 0 → 1 as the swipe approaches the commit
  // threshold. Past 0.45 we show the label so the operator gets
  // a calm preview of what's about to commit.
  const intensity = Math.min(1, Math.abs(progressX));
  const showLabel = Math.abs(progressX) > ACTIVE_PROGRESS;
  return (
    <div
      aria-hidden
      className={[
        "absolute inset-0 flex items-center pointer-events-none",
        isRight ? "justify-start pl-5" : "justify-end pr-5",
      ].join(" ")}
      style={{
        background: tone.bg,
        opacity: intensity,
        transition: "opacity 120ms ease-out",
      }}
    >
      <span
        className="inline-flex items-center gap-2 h-8 px-3 rounded-full text-[12px] font-semibold"
        style={{
          background: tone.chip,
          color: tone.fg,
          opacity: showLabel ? 1 : 0,
          transform: `scale(${showLabel ? 1 : 0.92})`,
          transition: "opacity 140ms ease-out, transform 140ms ease-out",
        }}
      >
        <Icon size={12} strokeWidth={2.5} />
        {label}
      </span>
    </div>
  );
}

/**
 * Generic div-based row wrapper. Use this for operational queue
 * rows rendered as flex/grid divs (e.g. Sales Progression).
 *
 * Props:
 *   - onProgressed       → fires on commit right-swipe
 *   - onRequestUpdate    → fires on commit left-swipe
 *   - className          → applied to the *foreground* row
 *   - children           → row contents (any JSX)
 *   - testid             → data-testid on the wrapper
 *   - disabled           → temporarily disable the gesture
 */
export function SwipeableRow({
  onProgressed,
  onRequestUpdate,
  onClick,
  className = "",
  children,
  testid,
  disabled = false,
}) {
  const coarse = useCoarsePointer();
  const enabled = coarse && !disabled;
  const swipe = useSwipeActions({
    enabled,
    onSwipeRight: onProgressed,
    onSwipeLeft:  onRequestUpdate,
  });

  if (!enabled) {
    // Desktop: render the row as-is. Zero visual side effects.
    return (
      <div className={className} data-testid={testid} onClick={onClick}>
        {children}
      </div>
    );
  }

  // Mobile: swallow the click only when the user just performed a
  // swipe so the row never accidentally navigates after a gesture.
  const swallowClick = (e) => {
    if (swipe.direction || Math.abs(swipe.deltaX) > 4) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.(e);
  };

  return (
    <div className="relative overflow-hidden" data-testid={testid}>
      <ActionBackdrop progressX={swipe.progressX} />
      <div
        ref={swipe.ref}
        className={className}
        style={{
          transform:  swipe.transform,
          transition: swipe.transition,
          willChange: "transform",
          touchAction: "pan-y", // permit vertical scroll, claim horizontal
        }}
        onClick={swallowClick}
        {...swipe.handlers}
      >
        {children}
      </div>
    </div>
  );
}
